import { getBackendUrl } from "../../App.vue";
// import * as jwt from 'jsonwebtoken'

export const userService = {
    login,
    resolveAccountType,
    verifySession,
    completeInternalOAuth,
    // Exposed for the topbar's session-loss path: when the CC360 cookie has
    // lapsed the Laser session usually has not, so re-running the handoff
    // signs the user straight back in without a prompt.
    startInternalOAuth,
    logout,
    clearClientIdentity,
};

// Async Login function call when "Login" is clicked
async function login(email, password, opts) {
    const normalizedEmail = String(email || '').trim();
    // forceCc360: explicit user intent to sign in with a CC360 customer
    // password. Emails that also exist as Odoo (internal) users normally
    // resolve to the Laser sign-in; this escape hatch keeps the customer
    // account reachable for people who hold both identities.
    let accountType;
    const requestedAccountType = opts && opts.accountType;
    if (opts && opts.forceCc360 === true) {
        accountType = 'cc360';
    } else if (requestedAccountType === 'odoo' || requestedAccountType === 'cc360') {
        accountType = requestedAccountType;
    } else {
        const resolution = await resolveAccountType(normalizedEmail);
        accountType = typeof resolution === 'string'
            ? resolution
            : resolution && resolution.auth_method;
    }

    if (accountType === 'odoo') {
        return startInternalOAuth(
            opts && opts.returnTo,
            normalizedEmail,
            opts && opts.onAuthorizationUrl,
        );
    }

    if (accountType !== 'cc360') {
        throw "No matching username/password found.";
    }
    if (!password) {
        throw "Password is required.";
    }

    var sha256 = require('js-sha256');
    var hash = sha256(password);

    const data = await fetch(`${getBackendUrl()}/login/${normalizedEmail},${hash}`, {
        method: "GET",
        headers: {
          Accept: "applicaiton/json",
          "Content-Type": "application/json",
        },
        withCredentials: true,
        credentials: 'include'
    })
    var user = ""
    await data.json().then(response => user = response)

    // Return promise whether or not credentials match
    if (user === "No matching username/password found.") {
        throw user;
    } else if (user === "No Fusion Team found.") {
        throw "Error logging in, user has no Fusion Team assigned.";
    } else if (user === "Successfully logged in.") {
        await localstorage();
        return JSON.stringify(user);
    } else {
        throw "No matching username/password found.";
    }
}

async function resolveAccountType(email) {
    const response = await fetch(`${getBackendUrl()}/login/resolve`, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
        credentials: 'include',
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw "Login is temporarily unavailable.";
    }
    return payload;
}

async function verifySession() {
    const response = await fetch(`${getBackendUrl()}/verify_cookie?avatar=1`, {
        method: "GET",
        headers: {
            Accept: "application/json",
        },
        credentials: 'include',
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || !Array.isArray(payload) || !payload[0] || !payload[0].email) {
        return null;
    }
    return payload[0];
}

async function startInternalOAuth(
    returnTo = "/?oauth=success&return_to=%2Framps#/login",
    email = "",
    onAuthorizationUrl,
) {
    const response = await fetch(`${getBackendUrl()}/login_odoo`, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            return_to: returnTo,
            email,
        }),
        credentials: 'include',
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload?.oauth_required || !payload.authorization_url) {
        if (payload?.error === 'oauth_unavailable') {
            throw 'Internal Laser sign-in is not configured.';
        }
        throw 'Internal Laser sign-in could not be started.';
    }
    if (typeof onAuthorizationUrl === 'function') {
        onAuthorizationUrl(payload.authorization_url);
    }
    window.location.assign(payload.authorization_url);
    return new Promise(() => {});
}

async function completeInternalOAuth() {
    const bootstrapped = await localstorage();
    if (!bootstrapped) {
        throw 'Internal sign-in could not be completed.';
    }
    const profile = await verifySession();
    if (!profile || profile.is_internal !== true) {
        throw 'Internal sign-in could not be completed.';
    }
    return profile;
}

function containsFusion(words) {
    if (!words) {
        return false;
    }

    if (typeof words === 'string') {
        words = words.indexOf(',') !== -1 ? words.split(',') : [words];
    } else if (Array.isArray(words)) {
        // words is already an array
    } else if (typeof words === 'object') {
        words = Object.values(words);
    } else {
        return false;
    }

    return words.some(word => String(word).toLowerCase() === "fusion");
}


async function localstorage(body) {
    try {
        // Fetch data from server
        const data = await fetch(`${getBackendUrl()}/setLocalStorage`, {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: body && JSON.stringify(body),
            withCredentials: true, 
            credentials: 'include' 
        });
        
        if (!data.ok) {
            throw new Error("Failed to fetch data");
        }

        const userdata = await data.json();
        // console.log("Fetched userdata:", userdata);

        // Set local storage
        window.localStorage.setItem("title", userdata[0]);
        window.localStorage.setItem("main_customer_name", userdata[0]);
        window.localStorage.setItem("full_name", userdata[1]);
        window.localStorage.setItem("type", userdata[2]);
        window.localStorage.setItem("additional_services", userdata[3]);
        window.localStorage.setItem("preferred_direction", userdata[4]);
        window.localStorage.setItem("list", userdata[6]);
        window.localStorage.setItem("logo", userdata[7]);
        window.localStorage.setItem("email", userdata[8]);
        window.localStorage.setItem("is_internal", userdata[9] === true ? "true" : "false");

        // Parse and log customer data first, so we can set a sensible country default.
        // Previously country was set to "" (empty string) which caused the dashboard
        // KPIs to show 0 on initial login — home.vue fires its data fetches before
        // horizontal-topbar.vue's async initializeCountryAndDivision() corrects it.
        const customer_data = JSON.parse(userdata[6]);
        // console.log("Parsed customer_data:", customer_data);

        const defaultCountry = customer_data && customer_data.length > 0
            ? customer_data[0].country
            : "";
        window.localStorage.setItem("country", defaultCountry);

        customer_data.forEach(element => {
            // console.log("Setting division for:", element.customer);
            window.localStorage.setItem("division", element.customer);
        });

        // Default to "All Companies" when multiple customers exist;
        // for a single customer just use it directly.
        if (customer_data && customer_data.length > 0) {
            const allCustomers = customer_data.map(c => c.customer).filter(Boolean);
            const unique = [...new Set(allCustomers)];
            if (unique.length > 1) {
              window.localStorage.setItem("division", unique.join(', '));
              window.localStorage.setItem("current_selection", "All Companies");
            } else {
              window.localStorage.setItem("division", unique[0]);
              window.localStorage.setItem("current_selection", unique[0]);
            }
        }

        if (containsFusion(userdata[3])) {
            window.localStorage.setItem("all_divisions", userdata[10]);
        }

        // Log IP address
        const userid = userdata[5];
        await fetch(`${getBackendUrl()}/log_ip/${userid}`, {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: body && JSON.stringify(body),
            withCredentials: true, 
            credentials: 'include' 
        });
        return true;
    } catch (error) {
        // console.error("Error in localstorage function:", error);
        return false;
    }
}



function clearClientIdentity() {
    [
        "division", "type", "full_name", "additional_services",
        "preferred_direction", "logo", "list", "country",
        "title", "main_customer_name", "all_divisions", "plant_filter",
        "email", "is_internal", "user", "current_selection",
    ].forEach((key) => window.localStorage.removeItem(key));
    // The /login route guard trusts auth/loggedIn, which is backed by this
    // legacy sessionStorage key. If it survives logout, the guard bounces
    // the logged-out user back home, home's verify_cookie then 401s and
    // auto-logs-out again — an endless /login <-> home redirect loop.
    try {
        window.sessionStorage.removeItem("authUser");
    } catch (_) {
        // storage unavailable — nothing else to do
    }
    try {
        window.sessionStorage.removeItem("auth.currentUser");
    } catch (_) {
        // storage unavailable — nothing else to do
    }
}

async function logout(body) {
    const data = await fetch(`${getBackendUrl()}/logout`, {
        method: "GET",
        headers: {
          Accept: "applicaiton/json",
          "Content-Type": "application/json",
        },
        body: body && JSON.stringify(body),
        withCredentials: true, 
        credentials: 'include' 
    })
    await data.json()

}
